import type { Category } from '../types';
import { FACTS } from '../facts';

const fee = FACTS.deliveryFeeUsd;
const lp = FACTS.lanePassPerDeliveryUsd;
const lpMax = FACTS.lanePassWeeklyMaxUsd;
const grace = FACTS.lanePassGraceDays;
const confirmHrs = FACTS.customerConfirmHours;

export const payStatement: Category = {
  slug: "reading-your-pay-statement",
  half: "work",
  accent: '#2E9E3A',
  icon: "dollar",
  label: { en: "Reading your pay statement", es: "Cómo leer tu estado de pagos" },
  blurb: {
    en: "Fees, tips, the lane-pass line, confirmation timing — and what to do if a number looks wrong.",
    es: "Tarifas, propinas, la línea del lane pass, los tiempos de confirmación — y qué hacer si un número no cuadra.",
  },
  guides: [
    {
      slug: "the-splits-line-by-line",
      related: [
        "reading-your-pay-statement/when-a-number-looks-wrong",
        "taxes-101/what-counts-as-income",
        "recordkeeping-and-money/set-aside-for-taxes",
      ],
      body: {
        en: {
          title: "The splits, line by line",
          summary: "Every line on your weekly statement, what it means, and where the number comes from.",
          sections: [
            {
              heading: "What each delivery pays",
              paragraphs: [
                `Each delivery pays you the full $${fee} delivery fee plus 100% of the tip. Grocery List Plus never takes a percentage of fees or tips. Not now, not later.`,
              ],
            },
            {
              heading: "The four lines",
              bullets: [
                `Delivery fees — the number of deliveries that became payout-eligible this week, times $${fee}.`,
                "Tips (100% yours) — every tip your customers left, in full.",
                `Lane pass — $${lp} per delivery, capped at $${lpMax} for the week.`,
                "Deposited — your earnings minus the lane pass. This is what lands in your bank.",
              ],
            },
            {
              heading: "How the lane pass works",
              bullets: [
                `$${lp} per delivery, never more than $${lpMax} in a week.`,
                "A zero-delivery week costs $0.",
                "Approved time off costs $0.",
                `Free for your first ${grace} days after go-live.`,
              ],
            },
            {
              heading: "When a delivery counts",
              paragraphs: [
                `A delivery becomes payout-eligible when your customer confirms receipt, or automatically ${confirmHrs} hours after delivery. So a Sunday-night delivery can land on next week's statement. That is timing, not a missing payment.`,
                `Payouts go out ${FACTS.payoutCadence.en} through Stripe Connect.`,
              ],
            },
            {
              heading: "Lines you may see sometimes",
              bullets: [
                "Comp — a late-pickup credit paid by Grocery List Plus.",
                "A tip added after delivery — added when it lands, sometimes on a later statement.",
              ],
            },
          ],
          actions: [
            "Open Earnings in the portal and find the four lines on your latest statement.",
            "Note your go-live date so you know when the lane pass starts.",
            "Check that Stripe Connect shows your correct bank account.",
          ],
        },
        es: {
          title: "Los desgloses, línea por línea",
          summary: "Cada línea de tu estado semanal, qué significa y de dónde sale el número.",
          sections: [
            {
              heading: "Lo que paga cada entrega",
              paragraphs: [
                `Cada entrega te paga la tarifa de entrega completa de $${fee} más el 100% de la propina. Lista de Compras Plus nunca se queda con un porcentaje de las tarifas ni de las propinas. Ni ahora ni después.`,
              ],
            },
            {
              heading: "Las cuatro líneas",
              bullets: [
                `Tarifas de entrega — el número de entregas que se volvieron elegibles para pago esta semana, por $${fee}.`,
                "Propinas (100% tuyas) — cada propina que dejaron tus clientes, completa.",
                `Lane pass (pase de carril) — $${lp} por entrega, con un tope de $${lpMax} por semana.`,
                "Depositado — tus ganancias menos el lane pass. Esto es lo que llega a tu banco.",
              ],
            },
            {
              heading: "Cómo funciona el lane pass",
              bullets: [
                `$${lp} por entrega, nunca más de $${lpMax} en una semana.`,
                "Una semana sin entregas cuesta $0.",
                "El tiempo libre aprobado cuesta $0.",
                `Gratis durante tus primeros ${grace} días después de activarte.`,
              ],
            },
            {
              heading: "Cuándo cuenta una entrega",
              paragraphs: [
                `Una entrega se vuelve elegible para pago cuando tu cliente confirma que la recibió, o automáticamente ${confirmHrs} horas después de la entrega. Por eso una entrega de domingo por la noche puede aparecer en el estado de la semana siguiente. Es cuestión de tiempos, no un pago faltante.`,
                `Los pagos salen de forma ${FACTS.payoutCadence.es} a través de Stripe Connect.`,
              ],
            },
            {
              heading: "Líneas que puedes ver a veces",
              bullets: [
                "Compensación — un crédito por recogida tardía que paga Lista de Compras Plus.",
                "Una propina agregada después de la entrega — se suma cuando llega, a veces en un estado posterior.",
              ],
            },
          ],
          actions: [
            "Abre Ganancias en el portal y ubica las cuatro líneas en tu estado más reciente.",
            "Anota tu fecha de activación para saber cuándo empieza el lane pass.",
            "Revisa que Stripe Connect muestre tu cuenta bancaria correcta.",
          ],
        },
      },
    },
    {
      slug: "when-a-number-looks-wrong",
      related: [
        "reading-your-pay-statement/the-splits-line-by-line",
        "reporting-a-problem/what-happens-next",
        "recordkeeping-and-money/set-aside-for-taxes",
      ],
      body: {
        en: {
          title: "When a number looks wrong",
          summary: "How to audit your statement in five minutes, and how to dispute a line from the portal.",
          sections: [
            {
              heading: "Audit us. We mean it.",
              paragraphs: [
                "Your statement is a claim Grocery List Plus is making about your week. Check it every time. You have the app; you have the numbers. If we got something wrong, we want to hear it.",
              ],
            },
            {
              heading: "The five-minute check",
              bullets: [
                `Recount your deliveries for the week in the app. Multiply by $${fee}. That should match the Delivery fees line.`,
                "Add up the tips shown on each delivery in the app. That should match Tips.",
                `Lane pass should be the smaller of $${lp} times your deliveries and $${lpMax}. It should be $0 on a zero-delivery week, during approved time off, or inside your first ${grace} days.`,
                "Deposited should equal fees plus tips plus any comp, minus the lane pass.",
              ],
            },
            {
              heading: "Before you dispute",
              bullets: [
                "A delivery from late Sunday may be on next week's statement.",
                `A delivery under ${confirmHrs} hours old that the customer has not confirmed is not eligible yet.`,
                "A tip added after delivery lands when it lands.",
              ],
            },
            {
              heading: "How to dispute",
              paragraphs: [
                "In the portal: Earnings → the statement → \"Something's wrong\". Say which line and what you expected. A human reviews it — not a bot. We aim to reply within 3 business days.",
                "If we were wrong, the correction shows on your next statement, and we tell you.",
              ],
            },
          ],
          actions: [
            "Run the five-minute check on your latest statement.",
            "Screenshot your delivery count and tip totals from the app before the week rolls over.",
            "If a line is off, file it from Earnings today rather than waiting.",
          ],
        },
        es: {
          title: "Cuando un número no cuadra",
          summary: "Cómo auditar tu estado en cinco minutos y cómo disputar una línea desde el portal.",
          sections: [
            {
              heading: "Audítanos. Lo decimos en serio.",
              paragraphs: [
                "Tu estado es una afirmación que Lista de Compras Plus hace sobre tu semana. Revísalo siempre. Tú tienes la app; tú tienes los números. Si nos equivocamos en algo, queremos saberlo.",
              ],
            },
            {
              heading: "La revisión de cinco minutos",
              bullets: [
                `Vuelve a contar tus entregas de la semana en la app. Multiplica por $${fee}. Eso debe coincidir con la línea de Tarifas de entrega.`,
                "Suma las propinas que aparecen en cada entrega en la app. Eso debe coincidir con Propinas.",
                `El lane pass (pase de carril) debe ser el menor entre $${lp} por tus entregas y $${lpMax}. Debe ser $0 en una semana sin entregas, durante tiempo libre aprobado o dentro de tus primeros ${grace} días.`,
                "Depositado debe ser tarifas más propinas más cualquier compensación, menos el lane pass.",
              ],
            },
            {
              heading: "Antes de disputar",
              bullets: [
                "Una entrega del domingo por la noche puede estar en el estado de la semana siguiente.",
                `Una entrega con menos de ${confirmHrs} horas que el cliente no ha confirmado todavía no es elegible.`,
                "Una propina agregada después de la entrega se suma cuando llega.",
              ],
            },
            {
              heading: "Cómo disputar",
              paragraphs: [
                "En el portal: Ganancias → el estado → \"Algo está mal\". Indica qué línea y qué esperabas. Lo revisa una persona, no un bot. Nuestra meta es responderte en un máximo de 3 días hábiles.",
                "Si nos equivocamos, la corrección aparece en tu siguiente estado y te avisamos.",
              ],
            },
          ],
          actions: [
            "Haz la revisión de cinco minutos en tu estado más reciente.",
            "Toma capturas de tu conteo de entregas y del total de propinas en la app antes de que cierre la semana.",
            "Si una línea no cuadra, repórtala desde Ganancias hoy mismo en lugar de esperar.",
          ],
        },
      },
    },
  ],
};
